import { cssVar } from './cssVar';

/**
 * Base spacing token — Tailwind v4's own `--spacing` custom property (0.25rem).
 * The rem fallback keeps the scale working in apps that don't load the Tailwind
 * stylesheet.
 */
export const SPACING_BASE = cssVar('--spacing', '0.25rem');

/**
 * Named spacing aliases, mirroring the documented component spacing scale
 * (`docs/Design System/06-spacing.md`). Each maps to a numeric step of the
 * Tailwind spacing scale, so `spacing('md')` and `spacing(3)` are equivalent.
 *
 * | alias   | step | rem      |
 * | ------- | ---- | -------- |
 * | `xxs`   | 0.5  | 0.125rem |
 * | `xs`    | 1    | 0.25rem  |
 * | `sm`    | 2    | 0.5rem   |
 * | `md`    | 3    | 0.75rem  |
 * | `lg`    | 4    | 1rem     |
 * | `xl`    | 6    | 1.5rem   |
 * | `xxl`   | 8    | 2rem     |
 */
export const SPACING_ALIAS_STEP = {
  xxs: 0.5,
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 6,
  xxl: 8,
} as const;

export type SpacingAlias = keyof typeof SPACING_ALIAS_STEP;
export type SpacingStep = number | SpacingAlias | 'px' | '0';

/**
 * Shortcut for a Tailwind spacing value, so styled-components can write
 * `padding: ${spacing(2.5)};` or `padding: ${spacing('md')};` instead of
 * `padding: ${({ theme }) => theme.tw.spacing[2.5]};`.
 *
 * **Conversion rule: `spacing(n)` === `n * 0.25rem` === `n / 4` rem.**
 * So `spacing(2)` → 0.5rem, `spacing(2.5)` → 0.625rem, `spacing(10)` → 2.5rem.
 *
 * Aliases are the same scale with a name — prefer them when one exists:
 * `xxs` 0.125rem · `xs` 0.25rem · `sm` 0.5rem · `md` 0.75rem · `lg` 1rem ·
 * `xl` 1.5rem · `xxl` 2rem.
 * `spacing('px')` is the 1px step, `spacing(0)` is `0`.
 *
 * Everything resolves to `calc(var(--spacing) * n)` — exactly how Tailwind
 * compiles its `p-2.5` / `gap-4` utilities — so styled-components share the same
 * runtime token as the Tailwind classes. The value is theme-independent (every
 * theme merges the same scale), so no `theme` access is required.
 */
export const spacing = (step: SpacingStep): string => {
  if (step === 0 || step === '0') {
    return '0';
  }

  // 1px === a quarter of the 0.25rem base, mirroring Tailwind's `px` step.
  if (step === 'px') {
    return `calc(${SPACING_BASE} * 0.25)`;
  }

  const resolvedStep =
    typeof step === 'string' ? SPACING_ALIAS_STEP[step] : step;

  return `calc(${SPACING_BASE} * ${resolvedStep})`;
};
